/**
 * Errores tipados que lanza `@alter/api-client`. Son `Error` "puros" — el
 * caller las distingue con `instanceof`. La política de side-effects en
 * `TokenStorage` ante `UnauthorizedError` vive **fuera** de este paquete:
 * el caller (apps/web) decide cuándo limpiar el storage y redirigir.
 */

/** Respuesta HTTP con estado 4xx/5xx (excepto 401, que tiene su propio tipo). */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly body?: unknown,
  ) {
    super(`HTTP ${status} ${statusText}`);
    this.name = 'HttpError';
  }
}

/**
 * HTTP 401. Indica token inválido o expirado. El backend NO documenta
 * endpoint de refresh (ver `02_CONTRATOS.md`); el caller debe limpiar el
 * `TokenStorage` y redirigir a login. `@alter/api-client` no lo hace
 * automáticamente — sin side-effects ocultos.
 */
export class UnauthorizedError extends Error {
  constructor(readonly body?: unknown) {
    super('Unauthorized — token inválido o expirado');
    this.name = 'UnauthorizedError';
  }
}

/** Falla de red / `fetch` rechazó (offline, DNS, CORS, etc.). */
export class NetworkError extends Error {
  override readonly cause: unknown;
  constructor(cause: unknown) {
    super('Network error');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Respuesta del backend no parsea con el schema esperado. Indica drift
 * entre `02_CONTRATOS.md` y la realidad — actualizar el schema en
 * `@alter/types` (y este paquete si corresponde) tras coordinar con backend.
 */
export class ValidationError extends Error {
  constructor(
    readonly issues: unknown,
    readonly raw: unknown,
  ) {
    super('Backend response did not match expected schema');
    this.name = 'ValidationError';
  }
}
